// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import Axios, { AxiosRequestConfig } from 'axios';
import mapValues from 'lodash/mapValues';

import { ExportInformationType, ImportInformationType, ReportInformationType } from '@shared/constants/data-management';
import { DataImport, DataImportDTO, ValidateInfoItem } from '@shared/models/data-management';
import { deserialize } from '@shared/utils/common';
import { createFormData } from '@modules/Private/submodules/DataManagement';

// Class to send requests to portal-api to Create reports/Import/export files.
@injectable()
export default class DataManagementService {
  static diToken = Symbol('data-management-service');
  private exportURLPrefix = '/data-service/export';
  private importURLPrefix = '/import';
  private reportURLPrefix = '/reports';

  // Note: All catch(err) removed.  Will be handled in the 'store' class.

  // Send request to generate (download from portal-api) a report.
  async reportData(reportType: ReportInformationType, queries: string, config: AxiosRequestConfig): Promise<Array<BlobPart> | undefined> {

    const response = await Axios.get<BlobPart>(
      this.reportURLPrefix,
      {
        ...config,
        params: { query: queries, custom: reportType == ReportInformationType.customerReport },
        responseType: 'blob'  // Fix problems where downloadFile() adds encoding bytes to data stream.
      }
    );

    console.log("*** Send report request:", config, response);

    return response ? [response.data] : undefined;
  }

  // Send request to export (download from portal-api) order data to a file.
  async exportData(exportDataType: ExportInformationType, config: AxiosRequestConfig): Promise<Array<BlobPart> | undefined> {
    console.log(`**** export: ${this.exportURLPrefix}, ${exportDataType}`, config);

    const response = await Axios.get<BlobPart>(
      this.exportURLPrefix,
      {
        ...config,
        params: { exportDataType }
      }
    );
    console.log("**** response: ", response);

    return response ? [response.data] : undefined;
  }

  // Send request to import (upload to portal-api) a file.
  async importData(file: File, type: ImportInformationType): Promise<void> {
    const formData = createFormData(file, type);

    return await Axios.post(this.importURLPrefix, formData, { // Apparently, newer version of Axios requires this header.
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Validate the import file (declare type vs actual content, ...)
  async validateImport(file: File, type: ImportInformationType): Promise<DataImport> {
    const formData = createFormData(file, type);

    // output debug msg
    formData.forEach((value, key) => console.log("*** import ", key, ": ", value));

    const { data } = await Axios.post(`${this.importURLPrefix}/validate`, formData, { // Apparently, newer version of Axios requires this header.
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const defaultValue: ValidateInfoItem = {
      amount: 0,
      fields: []
    };

    const serializedData = mapValues(data, (value) => deserialize(value, defaultValue)) as DataImportDTO;

    return new DataImport(serializedData);
  }
}
